import { useState } from "react";
import Description from "../Inputs/Description";
import Dropdown from "../Inputs/Dropdown";
import Amount from "../Inputs/Amount";
import Date from "../Inputs/Date";
import { BudgetBucketNodeItem, BudgetItem, BudgetType, BucketNodeItem, BucketNodeSnapshotResponse, ColorHexes, CurrentUser, DateTime, FamilyBucketPluralLabel, FamilyFeatureValue, FamilyHasAccess, SelectableItem, TimeframeItem, UserItem } from "../../types";
import { useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material";
import { Circle } from "@uiw/react-color";
import { ChevronDown } from "react-bootstrap-icons";
import Toggle from "../Inputs/Toggle";
import ModalFooterSaveCancel from "./ModalFooterSaveCancel";
import ModalHeader from "./ModalHeader";
import { Features } from "../../constants";

const EditBudget = (props: any) => {
	let [budget, setBudget] = useState(props.Item as BudgetItem);
	let timeframes: TimeframeItem[] = props.Timeframes;
	let users: UserItem[] = useSelector((state: any) => state.users.value);
	let user: CurrentUser = useSelector((state: any) => state.userData.value);
	const snapshot: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
	const mappedBucketDefinitions = (user.bucketDefinitions?.definitions ?? [])
		.filter(definition => definition.isEnabled && definition.requiresBudgetMapping)
		.sort((a, b) => a.sortOrder - b.sortOrder);
	const bucketNodesByType = mappedBucketDefinitions.reduce((result: Record<string, BucketNodeItem[]>, definition) => {
		result[definition.bucketTypeKey] = (snapshot?.nodes ?? [])
			.filter(node => node.bucketTypeKey.toLowerCase() === definition.bucketTypeKey.toLowerCase())
			.sort((a, b) => a.displayName.localeCompare(b.displayName));
		return result;
	}, {} as Record<string, BucketNodeItem[]>);
	const nodeById = (snapshot?.nodes ?? []).reduce((result: Record<number, BucketNodeItem>, node) => {
		result[node.bucketNodeId] = node;
		return result;
	}, {} as Record<number, BucketNodeItem>);

	const hasIncomeFeature = FamilyHasAccess(user, Features.Income);
	const budgetType = hasIncomeFeature ? (budget.budgetType ?? BudgetType.Expense) : BudgetType.Expense;
	const isIncomeBudget = budgetType === BudgetType.Income || budgetType === BudgetType.Combined;
	const isExpenseBudget = budgetType === BudgetType.Expense || budgetType === BudgetType.Combined;

	return (<>
		<ModalHeader Title={`${budget.budgetId > 0 ? 'Edit' : 'Add'} Budget`} CanDelete={props.OnDelete !== undefined} Close={props.Close} Delete={() => props.OnDelete(budget)} />
		<div className="modal-body mt-5">
			<Grid container>
				<Description Label="Title" Value={budget.description} OnChange={(val: any) => UpdateDescription(val.target.value)} Size={12} />
				{!isExpenseBudget && (
					<Amount Size={12} Label={"Spending Budget"} Value={budget.variant.amount} OnChange={(val: any) => UpdateAmount(val.target.value)} />
				)}
				{isIncomeBudget && (
					<Amount Size={12} Label="Income Budget" Value={budget.variant.incomeAmount} OnChange={(val: any) => UpdateIncomeAmount(val.target.value)} />
				)}
				<Dropdown Size={12}
					Class="mb-0"
					Label="Frequency"
					Options={timeframes
						.map(t => {
							return {
								Id: t.timeframeId,
								Value: t.longDescription
							} as SelectableItem;
						})}
					Index={0}
					Value={budget.timeframeId}
					OnChange={UpdateTimeframe}
					EnableNegative />
				{mappedBucketDefinitions.map(definition => (
					<Dropdown
						key={definition.bucketTypeKey}
						Size={12}
						Class="mb-0"
						Label={FamilyBucketPluralLabel(user, definition.bucketTypeKey, definition.pluralLabel || definition.bucketTypeKey)}
						Options={(bucketNodesByType[definition.bucketTypeKey] ?? []).map(node => ({
							Id: node.bucketNodeId,
							Value: node.displayName
						}) as SelectableItem)}
						Value={GetBucketNodeIds(budget, definition.bucketTypeKey)}
						Multiple
						OnChange={(newValue: number[]) => UpdateBucketNodes(definition.bucketTypeKey, newValue)} />
				))}
				<Dropdown Size={12}
					Class="mb-0"
					Label="Assigned Users"
					Options={users
						.map(u => {
							return {
								Id: u.userAccountId,
								Value: `${u.firstName} ${u.lastName}`
							} as SelectableItem;
						})}
					Index={0}
					Value={budget.userIds}
					Multiple
					OnChange={UpdateUsers} />
				<Date Size={12} Label="Start Date" OnChange={UpdateStartDate} Value={budget.variant.startDate} NoError />
				{budget.timeframeId === -3 &&
					<Date Size={12} Label="End Date (Optional)" OnChange={UpdateEndDate} NoError />
				}
				<Accordion className="w-100">
					<AccordionSummary expandIcon={<ChevronDown />}>
						<Typography>Advanced</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid item xs={12}>
							Color:
							<Circle color={budget.color} colors={ColorHexes} onChange={UpdateColor} />
						</Grid>
						<Toggle Size={12} Label="Do not require approval from unassigned users" OnChange={UpdateRequiresApproval} Checked={!budget.requiresApproval} className="my-3" />
						<Toggle Size={12} Label={`Exclude items in this budget from ${FamilyFeatureValue(user, Features.TransactionTitle)} page`} OnChange={UpdateIsViewTransactions} Checked={!budget.isViewTransactions} className="my-3" />
						{hasIncomeFeature && (
							<Dropdown
								Size={12}
								Class="mb-0"
								Label="Budget Type"
								Options={[
									{ Id: BudgetType.Expense, Value: "Expense" },
									{ Id: BudgetType.Income, Value: "Income" },
									{ Id: BudgetType.Combined, Value: "Combined" },
								]}
								Index={0}
								Value={budgetType}
								OnChange={UpdateBudgetType}
							/>
						)}
					</AccordionDetails>
				</Accordion>
			</Grid>
		</div>
		<ModalFooterSaveCancel ShowSave={ShouldShowSave()} Save={() => props.OnSave(budget)} Close={props.Close} />
	</>);

	function ShouldShowSave() {
		if (!budget.description) return false;
		return true;
	}

	function UpdateDescription(newValue: string) {
		var item = ({ ...budget, description: newValue });
		setBudget(item);
	}
	function UpdateAmount(newValue: string) {
		let val: number = 0;
		if (newValue) val = parseFloat(newValue);
		let variant = { ...budget.variant, amount: val };
		let item = ({ ...budget, variant: { ...variant } });
		setBudget(item);
	}
	function UpdateIncomeAmount(newValue: string) {
		let val: number = 0;
		if (newValue) val = parseFloat(newValue);
		let variant = { ...budget.variant, incomeAmount: val };
		let item = ({ ...budget, variant: { ...variant } });
		setBudget(item);
	}
	function UpdateTimeframe(newValue: any) {
		let variant = { ...budget.variant, endDate: null }
		let item = ({ ...budget, timeframeId: newValue, variant: { ...variant } });
		setBudget(item);
	}
	function UpdateBucketNodes(bucketTypeKey: string, newValue: number[]) {
		let item = ({ ...budget, bucketNodes: BuildBudgetBucketNodes(budget, bucketTypeKey, newValue, nodeById) });
		setBudget(item);
	}
	function UpdateUsers(newValue: any) {
		let item = ({ ...budget, userIds: newValue });
		setBudget(item);
	}
	function UpdateColor(newValue: any) {
		let item = ({ ...budget, color: newValue.hex });
		setBudget(item);
	}
	function UpdateRequiresApproval(event: any, newValue: boolean) {
		let item = ({ ...budget, requiresApproval: !newValue });
		setBudget(item);
	}
	function UpdateIsViewTransactions(event: any, newValue: boolean) {
		let item = ({ ...budget, isViewTransactions: !newValue });
		setBudget(item);
	}
	function UpdateBudgetType(newValue: any) {
		const nextType = newValue as BudgetType;
		let variant = { ...budget.variant };

		if (nextType === BudgetType.Income) {
			if ((variant.incomeAmount ?? 0) === 0 && (variant.amount ?? 0) > 0) {
				variant.incomeAmount = variant.amount;
			}
			variant.amount = 0;
		} else if (nextType === BudgetType.Expense) {
			if ((variant.amount ?? 0) === 0 && (variant.incomeAmount ?? 0) > 0) {
				variant.amount = variant.incomeAmount;
			}
			variant.incomeAmount = 0;
		}

		let item = ({ ...budget, budgetType: nextType, variant: { ...variant } });
		setBudget(item);
	}
	function UpdateStartDate(newValue: any) {
		let variant = { ...budget.variant, startDate: new DateTime(newValue) }
		let item = ({ ...budget, variant: { ...variant } });
		setBudget(item);
	}
	function UpdateEndDate(newValue: any) {
		let variant = { ...budget.variant, endDate: new DateTime(newValue) }
		let item = ({ ...budget, variant: { ...variant } });
		setBudget(item);
	}
}

function GetBucketNodeIds(budget: BudgetItem, bucketTypeKey: string) {
	return [...(budget.bucketNodes ?? [])]
		.filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase())
		.map(node => node.bucketNodeId)
		.sort((a, b) => a - b);
}

function BuildBudgetBucketNodes(budget: BudgetItem, bucketTypeKey: string, bucketNodeIds: number[], nodeById: Record<number, BucketNodeItem>) {
	const existingTypeNodes = (budget.bucketNodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase());
	const otherBucketNodes = (budget.bucketNodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() !== bucketTypeKey.toLowerCase());
	const nextTypeNodes = bucketNodeIds
		.map(bucketNodeId => {
			const existing = existingTypeNodes.find(node => node.bucketNodeId === bucketNodeId);
			if (existing) return existing;

			const bucketNode = nodeById[bucketNodeId];
			if (!bucketNode) return null;

			return {
				budgetBucketNodeId: 0,
				bucketNodeId: bucketNode.bucketNodeId,
				bucketTypeKey: bucketNode.bucketTypeKey,
				displayName: bucketNode.displayName,
				isInactive: bucketNode.isInactive,
				isViewTransactions: bucketNode.isViewTransactions,
				startDate: null,
				endDate: null,
			} as BudgetBucketNodeItem;
		})
		.filter((node): node is BudgetBucketNodeItem => node !== null);

	return [...otherBucketNodes, ...nextTypeNodes];
}

export default EditBudget;